/**
 * Tipos base do módulo genérico de Exportação de Dados
 * -----------------------------------------------------------------------------
 * Estrutura pensada para ser reaproveitada por QUALQUER módulo do sistema
 * (CRM/Negócios, Clientes, Tarefas, Cobranças...). Cada módulo registra uma
 * "fonte exportável" (ExportSource) declarando suas colunas e como buscar os
 * registros já filtrados pela tela.
 *
 * @version 1.3.0
 * RELEASE: 2026-09-12 v1.3.0 - Nova capacidade opcional resolveColumns: fontes
 *          cujas colunas dependem do registro selecionado (ex.: listas
 *          importadas, onde os campos variam conforme o arquivo) passam a
 *          informar as colunas ao abrir o modal.
 * RELEASE: 2026-09-07 v1.2.0 - Nova capacidade opcional describeParams: a fonte
 *          traduz os parâmetros da tela (filtros/ordenação) para linhas legíveis
 *          gravadas na auditoria (chave `_resumo` do log), evitando mostrar
 *          nomes técnicos ao administrador.
 * RELEASE: 2026-09-07 v1.1.0 - Fonte pode informar o total de registros (countRows)
 *          para o modal exibir o volume antes de exportar.
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial da base genérica de exportação.
 */

/** Formatos de arquivo suportados */
export type ExportFormat = 'csv' | 'xls';

/** Formas de entrega do arquivo ao usuário */
export type ExportDelivery = 'download' | 'email';

/** Tipo do valor de uma coluna (define a formatação aplicada no arquivo) */
export type ExportValueKind =
  | 'text'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'boolean';

/** Definição de uma coluna exportável */
export interface ExportColumn {
  /** Chave do campo dentro da linha retornada pela fonte */
  key: string;
  /** Rótulo exibido no modal e no cabeçalho do arquivo */
  label: string;
  /** Grupo lógico usado para organizar a seleção no modal */
  group: string;
  /** Tipo do valor (formatação) — padrão: 'text' */
  kind?: ExportValueKind;
  /** Marcada por padrão ao abrir o modal */
  defaultSelected?: boolean;
}

/**
 * Linha legível de um parâmetro da tela (filtro ou ordenação), gravada na
 * auditoria para o administrador entender exatamente o que foi exportado.
 */
export interface ExportParamSummary {
  /** Agrupa a linha no detalhe da exportação */
  group: 'filtro' | 'ordenacao';
  /** Rótulo amigável (ex.: "Situação") */
  label: string;
  /** Valor já traduzido (ex.: "Perdido") — nunca identificador interno */
  value: string;
}

/**
 * Fonte exportável de um módulo.
 * `P` é o formato dos parâmetros da tela (filtros/ordenação) — cada módulo
 * define o seu e o mesmo objeto é enviado ao servidor no envio por e-mail.
 */
export interface ExportSource<P = unknown> {
  /** Identificador usado nas permissões e nos logs (ex.: 'crm_deals') */
  id: string;
  /** Rótulo amigável do módulo */
  label: string;
  /** Título legível da tela exportada (usado no cabeçalho da planilha e no e-mail) */
  title?: string;
  /** Módulo ao qual a fonte pertence (usado na tela de perfis de permissão) */
  moduleKey: string;
  /** Nome base sugerido para o arquivo */
  fileBaseName: string;
  /** Colunas disponíveis para seleção (base, quando não há resolveColumns) */
  columns: ExportColumn[];
  /**
   * (Opcional) Resolve as colunas conforme o registro selecionado.
   * Usado por fontes com campos variáveis (ex.: cada lista importada tem as
   * colunas do próprio arquivo). Quando presente, o modal usa este resultado
   * no lugar de `columns`.
   */
  resolveColumns?: (tenantId: string, params: P) => Promise<ExportColumn[]>;
  /**
   * Busca as linhas já filtradas conforme a tela.
   * Deve devolver objetos "planos" cujas chaves correspondam às colunas.
   */
  fetchRows: (tenantId: string, params: P) => Promise<Record<string, unknown>[]>;
  /**
   * (Opcional) Conta quantos registros seriam exportados com os mesmos filtros.
   * Usado apenas para informar o volume no modal — nunca bloqueia a exportação.
   */
  countRows?: (tenantId: string, params: P) => Promise<number>;
  /**
   * (Opcional) Traduz os parâmetros da tela em linhas legíveis para a auditoria.
   * O resultado é gravado na chave `_resumo` do log de exportação; quando a
   * fonte não implementa, o log guarda apenas os parâmetros crus.
   */
  describeParams?: (tenantId: string, params: P) => Promise<ExportParamSummary[]>;
}

/** Teto de segurança de registros por exportação (compartilhado pelas fontes) */
export const EXPORT_ROW_LIMIT = 5000;
